/**
 * Session Service
 *
 * Form Engine Portal integration layer for @hsus-dev/session-policy SessionManager.
 * Wires the shared session library into FEP's MSAL logout, axios interceptor,
 * and Application Insights telemetry (`applicationInsights.service.ts`, HCP parity).
 *
 * Rules:
 *  - This is the ONLY file in FEP that imports SessionManager.
 *  - All other FEP code uses the exported functions below.
 *  - UI / App shell: import { SessionEvent } from '@hsus-dev/session-policy'.
 *
 * Installation prerequisite:
 *   npm install @hsus-dev/session-policy
 *   (GitHub Packages: `@hsus-dev` registry — see FormEnginePortal/frontend/.npmrc)
 */

import {
  SessionManager,
  DefaultSessionPolicy,
  type EndSessionOptions,
} from '@hsus-dev/session-policy';
import type {
  SessionState,
  SessionWarningDetail,
  SessionTransitionLog,
} from '@hsus-dev/session-policy';

import { TelemetryEvent } from '../constants/telemetryConstants';
import { registerApiActivityCallback } from './apiClient.service';
import { trackEvent } from './applicationInsights.service';

// ── Singleton ──────────────────────────────────────────────────────────────────
//
// One manager per browser tab. Never re-instantiate — startSession() is
// safe to call after a page refresh (it restores from localStorage timestamps).

const manager = new SessionManager();

// ── 1. Configure ──────────────────────────────────────────────────────────────
//
// FEP uses DefaultSessionPolicy: 30 min idle, 8h absolute, 5 min warning.
// Pass overrides here only if FEP has a documented, compliance-approved deviation.

// ── Idle timeout ───────────────────────────────────────────────────────────────
const _rawIdle = Number(import.meta.env.VITE_SESSION_IDLE_TIMEOUT_MINUTES);
const _idleOverride = Number.isFinite(_rawIdle) && _rawIdle > 0 ? _rawIdle : undefined;

// Scale idle warning proportionally (default ratio ≈ 1/6) so
// warningBeforeIdleMinutes < idleTimeoutMinutes is always satisfied.
const _effectiveIdle = _idleOverride ?? DefaultSessionPolicy.idleTimeoutMinutes;

// ── Absolute timeout ────────────────────────────────────────────────────────────
// Non-integer hours are allowed (e.g. 0.25 = 15 minutes, 0.01 ≈ 36 seconds for local testing).
function parsePositiveHoursFromEnv(raw: string | undefined): number | undefined {
  if (raw === undefined || String(raw).trim() === '') return undefined;
  const n = parseFloat(String(raw).trim());
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

const _absoluteOverride = parsePositiveHoursFromEnv(import.meta.env.VITE_SESSION_ABSOLUTE_TIMEOUT_HOURS);

// Scale absolute warning proportionally so warningBeforeAbsoluteMinutes < absoluteTimeoutHours*60.
// Uses the same 1/6 ratio, capped at the default 5 min.
const _effectiveAbsolute = _absoluteOverride ?? DefaultSessionPolicy.absoluteTimeoutHours;

const idlePartial =
  _idleOverride === undefined
    ? {}
    : {
        idleTimeoutMinutes: _idleOverride,
        warningBeforeIdleMinutes: Math.min(
          DefaultSessionPolicy.warningBeforeIdleMinutes,
          _effectiveIdle / 6
        ),
      };

const absolutePartial =
  _absoluteOverride === undefined
    ? {}
    : {
        absoluteTimeoutHours: _absoluteOverride,
        warningBeforeAbsoluteMinutes: Math.min(
          DefaultSessionPolicy.warningBeforeAbsoluteMinutes,
          (_effectiveAbsolute * 60) / 6
        ),
      };

manager.initialize({
  ...idlePartial,
  ...absolutePartial,
  enableCrossTabSync: true,
  enableActivityTracking: true,
});

// ── 2. Wire Application Insights telemetry ────────────────────────────────────
//
// Every state transition (Active → Warning, Warning → Expired, etc.) is sent
// as a custom event in production when Application Insights is configured (connection string or instrumentation key).

manager.onTransition((log: SessionTransitionLog) => {
  trackEvent(TelemetryEvent.SESSION_TRANSITION, {
    from: log.from,
    to: log.to,
    event: log.event,
    sessionId: log.sessionId,
    timestamp: String(log.timestamp),
  });
});

// ── 3. Wire axios activity hook ────────────────────────────────────────────────
//
// Registers a callback into apiClient.service.ts that fires on every outbound
// request across ALL axios instances created by this module — primary apiClient
// and every factory-created client. This prevents any secondary client from
// bypassing the idle timer.
//
// registerApiActivityCallback(undefined) removes it (useful in tests).

registerApiActivityCallback(() => manager.recordActivity());

// ── Public API ─────────────────────────────────────────────────────────────────

/**
 * Register FEP's logout implementation with the session manager.
 *
 * Call ONCE from AuthContext before startSession().
 * The callback receives control when the session expires, times out, or the
 * user logs out — the library never imports MSAL directly.
 */
export function registerSessionLogoutCallback(callback: (reason: string) => void): void {
  manager.onLogout(callback);
}

/**
 * Start the session after successful MSAL authentication.
 *
 * Call from the MSAL login-success handler (handleRedirectPromise result
 * or acquireTokenSilent confirmation). Safe to call on page refresh —
 * the manager restores timer positions from persisted localStorage timestamps.
 */
export function startSession(): void {
  manager.startSession();
}

/**
 * Tear down session timers and hsus_* storage. Call on user sign-out before clearing
 * browser storage so the next login gets a fresh absolute deadline.
 * Use `invokeLogoutCallback: false` when the portal is already executing its logout path.
 */
export function endSession(reason: string = 'user', options?: EndSessionOptions): void {
  manager.endSession(reason, options);
}

export type { EndSessionOptions };

/**
 * Returns the current session state.
 *
 * Use in route guards to block navigation to protected routes when the
 * session is Expired or Unauthenticated.
 */
export function getSessionState(): SessionState {
  return manager.getSessionState();
}

/**
 * Signal HTTP activity while the session is **Active** (secondary axios instances).
 *
 * The primary apiClient interceptor above already covers the default instance.
 * Does not dismiss the idle warning modal — use {@link continueSession} for that.
 */
export function recordApiActivity(): void {
  manager.recordActivity();
}

/**
 * Acknowledge the idle warning and return the session to Active.
 * Call from the "Continue Working" button in the session warning modal.
 * Uses acknowledgeIdleWarning() — not recordActivity() (HTTP must not dismiss the modal).
 */
export function continueSession(): void {
  manager.acknowledgeIdleWarning();
}

// Event names: import { SessionEvent } from '@hsus-dev/session-policy' in UI / App shell.

export type { SessionWarningDetail };
